import { v4 as uuidv4 } from 'uuid';
import { makeLabel, normalizePlan, rotatePoint, ROW_LABEL_DISTANCE, SEAT_RADIUS } from './geometry';
import { buildRow, createTable, fitTableSize } from './planOps';
import { createObject, createSectionZone, sectionSize } from './sections';
import type { Category, Plan, Point, Row, Stage, TableShape, Zone } from './types';

export type SeatMapTemplate = {
    id: string;
    name: string;
    description: string;
    build: () => Plan;
};

const SPACING = 25;
const ROW_GAP = 32;

class Builder {
    zones: Zone[] = [];
    stages: Stage[] = [];
    private tables = 0;

    private name: string;
    private categories: Category[];

    constructor(name: string, categories: Category[]) {
        this.name = name;
        this.categories = categories;
    }

    zone(name: string, code: string): number {
        this.zones.push({ name, code, position: { x: 0, y: 0 }, rows: [] });
        return this.zones.length - 1;
    }

    row(zi: number, points: Point[], label: string, category = '') {
        if (!points.length) return;
        this.zones[zi].rows.push(buildRow(this.zones[zi], points, label, 'numeric', category));
    }

    table(zi: number, center: Point, count: number, shape: TableShape = 'round', category = '', size?: { width: number; height: number }) {
        const { row, stage } = createTable(this.zones[zi], center, {
            count,
            shape,
            spacing: SPACING,
            label: `T${++this.tables}`,
            seatScheme: 'numeric',
            category,
            size,
        });
        this.zones[zi].rows.push(row as Row);
        this.stages.push(stage);
    }

    rect(name: string, x: number, y: number, width: number, height: number, color = '#FDE68A', stroke = '#D97706', radius = 8) {
        this.stages.push({
            id: uuidv4(), name, type: 'square', position: { x, y }, size: { width, height },
            rotation: 0, color, strokeColor: stroke, strokeWidth: 2, curveRadius: radius,
        });
    }

    ellipse(name: string, cx: number, cy: number, rx: number, ry: number, color = '#FDE68A', stroke = '#D97706') {
        this.stages.push({
            id: uuidv4(), name, type: 'circle', position: { x: cx - rx, y: cy - ry }, size: { width: rx * 2, height: ry * 2 },
            rotation: 0, color, strokeColor: stroke, strokeWidth: 2,
        });
    }

    polygon(name: string, abs: Point[], color = '#FDE68A', stroke = '#D97706') {
        const minX = Math.min(...abs.map(p => p.x));
        const minY = Math.min(...abs.map(p => p.y));
        const maxX = Math.max(...abs.map(p => p.x));
        const maxY = Math.max(...abs.map(p => p.y));
        this.stages.push({
            id: uuidv4(), name, type: 'polygon', position: { x: minX, y: minY }, size: { width: maxX - minX, height: maxY - minY },
            points: abs.map(p => ({ x: p.x - minX, y: p.y - minY })), rotation: 0, color, strokeColor: stroke, strokeWidth: 2,
        });
    }

    build(): Plan {
        return normalizePlan({ name: this.name, categories: this.categories, zones: this.zones, stages: this.stages, entrances: [] });
    }
}

const straight = (cx: number, y: number, count: number): Point[] =>
    Array.from({ length: count }, (_, i) => ({ x: cx + (i - (count - 1) / 2) * SPACING, y }));

const arc = (c: Point, r: number, fromDeg: number, toDeg: number): Point[] => {
    const span = Math.abs(toDeg - fromDeg);
    const n = Math.max(1, Math.floor((r * span * Math.PI) / 180 / SPACING) + 1);
    return Array.from({ length: n }, (_, i) => {
        const deg = n === 1 ? (fromDeg + toDeg) / 2 : fromDeg + ((toDeg - fromDeg) * i) / (n - 1);
        const a = (deg * Math.PI) / 180;
        return { x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) };
    });
};

const AISLE = 20;

const section = (c: Point, r: number, fromDeg: number, toDeg: number): Point[] => {
    const trim = ((AISLE / r) * 180) / Math.PI;
    const dir = Math.sign(toDeg - fromDeg) || 1;
    return arc(c, r, fromDeg + dir * trim, toDeg - dir * trim);
};

const pick = <T,>(i: number, thresholds: [number, T][], fallback: T): T => {
    for (const [limit, value] of thresholds) if (i < limit) return value;
    return fallback;
};

const theater = (): Plan => {
    const b = new Builder('Theater', [
        { name: 'Premium', color: '#8B5CF6' },
        { name: 'Standard', color: '#3B82F6' },
        { name: 'Balcony', color: '#10B981' },
    ]);
    b.rect('Stage', -220, -90, 440, 70);
    const c = { x: 0, y: -320 };
    const sections: [string, string, number, number][] = [
        ['Orchestra Left', 'OL', 126, 104],
        ['Orchestra Center', 'OC', 104, 76],
        ['Orchestra Right', 'OR', 76, 54],
    ];
    sections.forEach(([name, code, from, to]) => {
        const zi = b.zone(name, code);
        for (let i = 0; i < 12; i++) {
            const premium = code === 'OC' && i < 5;
            b.row(zi, section(c, 400 + i * ROW_GAP, from, to), makeLabel(i, 'upper'), premium ? 'Premium' : 'Standard');
        }
    });
    const balcony: [string, string, number, number][] = [
        ['Balcony Left', 'BL', 130, 104],
        ['Balcony Center', 'BC', 104, 76],
        ['Balcony Right', 'BR', 76, 50],
    ];
    balcony.forEach(([name, code, from, to]) => {
        const zi = b.zone(name, code);
        for (let i = 0; i < 6; i++) b.row(zi, section(c, 400 + 12 * ROW_GAP + 70 + i * ROW_GAP, from, to), makeLabel(i, 'upper', 13), 'Balcony');
    });
    return b.build();
};

const arena = (): Plan => {
    const b = new Builder('Arena (in the round)', [
        { name: 'Floor', color: '#EF4444' },
        { name: 'Lower bowl', color: '#3B82F6' },
    ]);
    b.rect('Stage', -90, -90, 180, 180);
    ([['South', 'S', 0], ['West', 'W', 90], ['North', 'N', 180], ['East', 'E', 270]] as const).forEach(([name, code, rot]) => {
        const zi = b.zone(`Section ${name}`, code);
        for (let i = 0; i < 10; i++) {
            const pts = straight(0, 140 + i * ROW_GAP, 10 + i * 2).map(p => rotatePoint(p, { x: 0, y: 0 }, rot));
            b.row(zi, pts, String(i + 1), i < 3 ? 'Floor' : 'Lower bowl');
        }
    });
    return b.build();
};

const amphitheater = (): Plan => {
    const b = new Builder('Amphitheater', [
        { name: 'Front', color: '#F59E0B' },
        { name: 'Middle', color: '#3B82F6' },
        { name: 'Back', color: '#64748B' },
    ]);
    b.polygon('Stage', Array.from({ length: 17 }, (_, i) => ({ x: 110 * Math.cos((i * Math.PI) / 16), y: 110 * Math.sin((i * Math.PI) / 16) })));
    const wedges: [number, number][] = [[165, 130], [130, 90], [90, 50], [50, 15]];
    wedges.forEach(([from, to], wi) => {
        const zi = b.zone(`Section ${wi + 1}`, `S${wi + 1}`);
        for (let i = 0; i < 14; i++) {
            b.row(zi, section({ x: 0, y: 0 }, 150 + i * 34, from, to), String(i + 1), pick(i, [[4, 'Front'], [9, 'Middle']], 'Back'));
        }
    });
    return b.build();
};

const gala = (): Plan => {
    const b = new Builder('Gala dinner', [
        { name: 'VIP', color: '#F59E0B' },
        { name: 'General', color: '#3B82F6' },
    ]);
    b.rect('Stage', -180, -330, 360, 80);
    b.rect('Dance floor', -150, -230, 300, 150, '#E0E7FF', '#6366F1', 4);
    const zi = b.zone('Banquet Hall', 'BH');
    for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 5; c++) {
            b.table(zi, { x: (c - 2) * 160, y: 40 + r * 160 }, 10, 'round', r === 0 ? 'VIP' : 'General');
        }
    }
    b.table(zi, { x: -330, y: -160 }, 8, 'rect', 'VIP');
    b.table(zi, { x: 330, y: -160 }, 8, 'rect', 'VIP');
    return b.build();
};

const cabaret = (): Plan => {
    const b = new Builder('Cabaret / Comedy club', [
        { name: 'Table', color: '#EC4899' },
        { name: 'Bar stool', color: '#14B8A6' },
    ]);
    b.rect('Stage', -160, -200, 320, 70);
    const zi = b.zone('Main Room', 'MR');
    for (let r = 0; r < 4; r++) {
        const offset = r % 2 ? 55 : 0;
        for (let c = 0; c < (r % 2 ? 5 : 6); c++) {
            b.table(zi, { x: -275 + offset + c * 110, y: -60 + r * 105 }, 4, r === 3 ? 'triangle' : 'round', 'Table');
        }
    }
    b.rect('Bar', -260, 420, 520, 40, '#E5E7EB', '#6B7280', 6);
    const bar = b.zone('Bar', 'BAR');
    b.row(bar, straight(0, 395, 18), 'Bar', 'Bar stool');
    return b.build();
};

const conference = (): Plan => {
    const b = new Builder('Conference', [
        { name: 'Reserved', color: '#8B5CF6' },
        { name: 'General', color: '#3B82F6' },
    ]);
    b.rect('Podium', -160, -150, 320, 60);
    ([['Left', 'L', -170], ['Right', 'R', 170]] as const).forEach(([name, code, cx]) => {
        const zi = b.zone(`${name} Block`, code);
        for (let i = 0; i < 12; i++) b.row(zi, straight(cx, i * ROW_GAP, 12), makeLabel(i, 'upper'), i < 2 ? 'Reserved' : 'General');
    });
    return b.build();
};

const wedding = (): Plan => {
    const b = new Builder('Wedding ceremony', [
        { name: 'Family', color: '#EC4899' },
        { name: 'Guests', color: '#A3A3A3' },
    ]);
    b.ellipse('Altar', 0, -130, 90, 40, '#FCE7F3', '#DB2777');
    b.rect('Aisle', -35, -60, 70, 330, '#FFF1F2', '#FDA4AF', 4);
    ([["Bride's side", 'B', -125], ["Groom's side", 'G', 125]] as const).forEach(([name, code, cx]) => {
        const zi = b.zone(name, code);
        for (let i = 0; i < 9; i++) b.row(zi, straight(cx, -40 + i * ROW_GAP, 6), String(i + 1), i < 2 ? 'Family' : 'Guests');
    });
    return b.build();
};

const stadium = (): Plan => {
    const b = new Builder('Stadium', [
        { name: 'Sideline', color: '#EF4444' },
        { name: 'Endzone', color: '#3B82F6' },
    ]);
    b.rect('Field', -330, -200, 660, 400, '#BBF7D0', '#16A34A', 4);
    const sides: [string, string, number, boolean][] = [
        ['North Stand', 'N', 180, true],
        ['South Stand', 'S', 0, true],
        ['East Stand', 'E', 270, false],
        ['West Stand', 'W', 90, false],
    ];
    sides.forEach(([name, code, rot, long]) => {
        const zi = b.zone(name, code);
        const base = long ? 240 : 370;
        for (let i = 0; i < (long ? 10 : 8); i++) {
            const pts = straight(0, base + i * ROW_GAP, long ? 26 + i : 14 + i).map(p => rotatePoint(p, { x: 0, y: 0 }, rot));
            b.row(zi, pts, String(i + 1), long ? 'Sideline' : 'Endzone');
        }
    });
    return b.build();
};

export const TEMPLATES: SeatMapTemplate[] = [
    { id: 'theater', name: 'Theater', description: 'Curved orchestra in three sections plus a balcony', build: theater },
    { id: 'conference', name: 'Conference', description: 'Straight rows in two blocks with a center aisle', build: conference },
    { id: 'gala', name: 'Gala dinner', description: 'Round tables of 10, head tables and a dance floor', build: gala },
    { id: 'cabaret', name: 'Cabaret', description: 'Small staggered tables near the stage and bar seating', build: cabaret },
    { id: 'arena', name: 'Arena in the round', description: 'Center stage with four sections around it', build: arena },
    { id: 'amphitheater', name: 'Amphitheater', description: 'Semicircular tiers split into four wedges', build: amphitheater },
    { id: 'stadium', name: 'Stadium', description: 'Field with stands on all four sides', build: stadium },
    { id: 'wedding', name: 'Wedding ceremony', description: 'Two sides of rows with an aisle to the altar', build: wedding },
];

export type PatternType = 'tables' | 'sections' | 'mixed';
export type PatternExtra = 'stage' | 'food' | 'exit' | 'bathroom' | 'bar' | 'dance';

export const PATTERN_EXTRAS: { id: PatternExtra; label: string }[] = [
    { id: 'stage', label: 'Stage' },
    { id: 'food', label: 'Food' },
    { id: 'exit', label: 'Exit' },
    { id: 'bathroom', label: 'Bathroom' },
    { id: 'bar', label: 'Bar' },
    { id: 'dance', label: 'Dance' },
];

export type PatternOptions = {
    type: PatternType;
    sections: number;
    rowsPerSection: number;
    seatsPerRow: number;
    tables: number;
    seatsPerTable: number;
    tableShape: TableShape;
    extras: PatternExtra[];
};

export const patternCapacity = (o: PatternOptions) =>
    (o.type === 'tables' ? 0 : o.sections * o.rowsPerSection * o.seatsPerRow) + (o.type === 'sections' ? 0 : o.tables * o.seatsPerTable);

const PATTERN_GAP = 60;
const TABLE_GAP = 50;
const SEAT_PAD = 15;

const gridColumns = (n: number, w: number, h: number, ratio = 1.6) => {
    let best = 1;
    let score = Infinity;
    for (let cols = 1; cols <= n; cols++) {
        const rows = Math.ceil(n / cols);
        const s = Math.abs(Math.log(((cols * w) / (rows * h)) / ratio));
        if (s < score) {
            score = s;
            best = cols;
        }
    }
    return best;
};

const gridCenters = (n: number, cellW: number, cellH: number, top: number): { centers: Point[]; width: number; height: number } => {
    if (n <= 0) return { centers: [], width: 0, height: 0 };
    const cols = gridColumns(n, cellW, cellH);
    const rows = Math.ceil(n / cols);
    const centers = Array.from({ length: n }, (_, i) => {
        const r = Math.floor(i / cols);
        const inRow = r === rows - 1 ? n - r * cols : cols;
        const c = i - r * cols;
        return { x: (c - (inRow - 1) / 2) * cellW, y: top + r * cellH + cellH / 2 };
    });
    return { centers, width: cols * cellW, height: rows * cellH };
};

export const buildPattern = (o: PatternOptions, spacing = SPACING, rowSpacing = 30): Plan => {
    const zones: Zone[] = [];
    const stages: Stage[] = [];
    const has = (e: PatternExtra) => o.extras.includes(e);
    const withSections = o.type !== 'tables' && o.sections > 0;
    const withTables = o.type !== 'sections' && o.tables > 0;
    let label = 0;
    let y = 0;

    const sec = sectionSize({ rows: o.rowsPerSection, seatsPerRow: o.seatsPerRow, seatSpacing: spacing, rowSpacing });
    const secCell = { w: sec.width + ROW_LABEL_DISTANCE * 2 + PATTERN_GAP, h: sec.height + PATTERN_GAP + 30 };
    const tableSize = fitTableSize(o.tableShape, o.seatsPerTable, spacing, undefined, o.tableShape === 'rect' ? Math.min(2, o.seatsPerTable) : 0);
    const tableCell = { w: tableSize.width + (SEAT_RADIUS + SEAT_PAD) * 2 + TABLE_GAP, h: tableSize.height + (SEAT_RADIUS + SEAT_PAD) * 2 + TABLE_GAP };
    const secGrid = withSections ? gridCenters(o.sections, secCell.w, secCell.h, 0) : null;
    const tableGrid = withTables ? gridCenters(o.tables, tableCell.w, tableCell.h, 0) : null;
    const contentW = Math.max(secGrid?.width ?? 0, tableGrid?.width ?? 0, 400);

    const object = (name: string, icon: string, c: Point, w: number, h: number) => {
        const s = createObject(c, { shape: 'square', name, icon, showIcon: true });
        stages.push({ ...s, position: { x: Math.round(c.x - w / 2), y: Math.round(c.y - h / 2) }, size: { width: w, height: h } });
    };

    if (has('stage')) {
        const w = Math.round(Math.min(Math.max(300, contentW * 0.5), 700));
        object('Stage', 'stage', { x: 0, y: y + 45 }, w, 90);
        y += 90 + PATTERN_GAP;
    }
    if (has('dance')) {
        object('Dance floor', 'dance', { x: 0, y: y + 80 }, 280, 160);
        y += 160 + PATTERN_GAP;
    }
    const seatingTop = y;

    if (secGrid) {
        secGrid.centers.forEach(c => {
            zones.push(createSectionZone(String(++label), { x: c.x, y: y + c.y + 15 }, {
                rows: o.rowsPerSection,
                seatsPerRow: o.seatsPerRow,
                seatSpacing: spacing,
                rowSpacing,
            }));
        });
        y += secGrid.height;
    }
    if (tableGrid) {
        const zone: Zone = { name: 'Tables', code: 'TB', position: { x: 0, y: 0 }, rows: [] };
        tableGrid.centers.forEach(c => {
            const { row, stage } = createTable(zone, { x: c.x, y: y + c.y }, {
                count: o.seatsPerTable,
                shape: o.tableShape,
                spacing,
                label: String(++label),
                seatScheme: 'numeric',
                endSeats: o.tableShape === 'rect' ? Math.min(2, o.seatsPerTable) : 0,
            });
            zone.rows.push(row as Row);
            stages.push(stage);
        });
        zones.push(zone);
        y += tableGrid.height;
    }

    const midY = (seatingTop + y) / 2;
    const side = contentW / 2 + 110;
    if (has('food')) object('Food', 'food', { x: -side, y: midY }, 140, 90);
    if (has('bar')) object('Bar', 'bar', { x: side, y: midY }, 140, 90);
    if (has('exit')) object('Exit', 'exit', { x: -contentW / 4, y: y + 50 }, 140, 60);
    if (has('bathroom')) object('Bathroom', 'bathroom', { x: contentW / 4, y: y + 50 }, 140, 60);

    return normalizePlan({
        name: o.type === 'tables' ? 'Tables and chairs' : o.type === 'sections' ? 'Sections and rows' : 'Mixed seating',
        categories: [
            { name: 'General', color: '#3B82F6' },
            { name: 'VIP', color: '#F59E0B' },
            { name: 'Premium', color: '#8B5CF6' },
        ],
        zones,
        stages,
        entrances: [],
    });
};
